import type {
  ChallengeAPIResponse,
  RepliesResponse,
  UserDetails,
} from '@assembly-web/services';
import { NoRewardsFound as PostNotFound } from '@assembly-web/ui/lib/UI/assets/images';
import { EmptyOrErrorStateTemplate } from '@assembly-web/ui/lib/UI/Web/PostAndReplies/EmptyOrErrorStateTemplate';

import { useGetChallengeDetailsByIdQuery } from '../../../hooks/challenges/useGetChallengeDetailsByIdQuery';
import { useUserDetails } from '../../../hooks/useUserDetails';
import type { ChallengesDrawer } from '../types';
import { ChallengeContainer } from './ChallengeContainer';

function ChallengeDrawerLoader() {
  return (
    <div className="m-4 w-full animate-pulse items-start">
      <div className="flex justify-between">
        <div className="flex items-center gap-1">
          <div className="h-[12px] w-[79px] rounded-full bg-gray-5"></div>
          <div className="h-4 w-4 rounded bg-gray-5"></div>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-[12px] w-[79px] rounded-full bg-gray-5"></div>
          <div className="h-4 w-4 rounded bg-gray-5"></div>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <div className="h-[32px] w-full max-w-[482px] flex-shrink-0 rounded bg-gray-5"></div>
        <div className="h-[192px] w-full max-w-[182px] flex-shrink-0 rounded bg-gray-5"></div>
        <div className="h-[24px] w-[208px] flex-shrink-0 rounded bg-gray-5"></div>
        <div className="h-[24px] w-[482px] flex-shrink-0 rounded bg-gray-5"></div>
      </div>
    </div>
  );
}

function ChallengeDrawerError() {
  return (
    <section className="flex h-full items-center justify-center self-auto px-6 pb-4">
      <EmptyOrErrorStateTemplate
        image={PostNotFound}
        altText="flow-response-loading-error"
        heading="Challenge not found"
        subHeading={`It may have been deleted or you may have lost access to it. Please confirm that you are logged into the correct account.`}
      />
    </section>
  );
}

type ChallengeDrawerContentProps = {
  userDetails: UserDetails;
  repliesData: RepliesResponse;
  challengeDetails: ChallengeAPIResponse;
};

function ChallengeDrawerContent({
  repliesData,
  userDetails,
  challengeDetails,
}: ChallengeDrawerContentProps) {
  return (
    <section className="h-full">
      <ChallengeContainer
        repliesData={repliesData}
        userDetails={userDetails}
        challengeDetails={challengeDetails}
      />
    </section>
  );
}

export function ChallengeDrawerBody({
  data: { challengeId },
}: ChallengesDrawer) {
  const [{ data: userDetails, isLoading: isUserDetailsLoading }] =
    useUserDetails();

  const {
    data: challengeDetails,
    error,
    isLoading: isChallengeDetailsLoading,
  } = useGetChallengeDetailsByIdQuery(challengeId);

  const repliesData = {
    count: 1,
    lastRepliedAt: 'ssss',
    respondentsCount: 2,
    initialRespondents: [
      {
        name: 'Sangeeth',
        memberID: '112',
      },
    ],
  };

  if (isChallengeDetailsLoading || isUserDetailsLoading) {
    return <ChallengeDrawerLoader />;
  }

  if (error) {
    return <ChallengeDrawerError />;
  }

  if (challengeDetails && userDetails) {
    return (
      <ChallengeDrawerContent
        repliesData={repliesData}
        userDetails={userDetails}
        challengeDetails={challengeDetails}
      />
    );
  }

  return <ChallengeDrawerError />;
}
